import {
  ApiError,
  HttpError,
  NetworkError,
  TimeoutError,
  AuthenticationError,
  RequestCancelledError,
  ResponseParseError,
} from './apiErrors';
import type { ApiErrorKind } from './apiErrors';

// RTK Query stores errors in Redux: retain useful fields, not Error instances
// or their potentially non-serializable causes.
export interface ApiQueryError {
  kind: ApiErrorKind | 'unknown';
  message: string;
  status?: number;
}

export const toApiQueryError = (error: unknown): ApiQueryError => ({
  kind: error instanceof ApiError ? error.kind : 'unknown',
  message: error instanceof Error ? error.message : 'Request failed',
  ...(error instanceof HttpError ? { status: error.status } : {}),
});

// Imperative saga consumers still use instanceof for retry/offline decisions.
export const fromApiQueryError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  if (typeof error !== 'object' || error === null)
    return new Error('Request failed');
  const message =
    'message' in error && typeof error.message === 'string'
      ? error.message
      : 'Request failed';
  if ('kind' in error) {
    switch (error.kind) {
      case 'network':
        return new NetworkError(message);
      case 'timeout':
        return new TimeoutError(message);
      case 'authentication':
        return new AuthenticationError(message);
      case 'cancelled':
        return new RequestCancelledError(message);
      case 'response':
        return new ResponseParseError(message);
      case 'http':
        if ('status' in error && typeof error.status === 'number')
          return new HttpError(error.status, message);
    }
  }
  if ('name' in error && error.name === 'AbortError')
    return new RequestCancelledError(message);
  return new Error(message);
};
